import React, { useMemo } from 'react'
import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { isCemFeatureEnabled } from 'src/rsmCoreComponents/utils/featureFlagUtils'
import { CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT } from 'src/utils/constants/constants'
import { getCemFeatures } from 'src/store/userInfo/userInfoSlice'
import { useSelector, useDispatch } from 'react-redux'
import {
  ImplementAutoPayLimitType,
  setImplementAutoPayLimit,
} from 'src/store/invoices/invoiceSelectedInvoicesSlice'
import {
  SelectedAccountTypeState,
  setAccountSelectedType,
  setDoNotSave,
  setNewPaymentMethod,
  setPayerPaymentAccountsModel,
  setSelectedBankAccount,
  setSelectedCreditCard,
  setSelectedPayer,
} from '../../../store/invoices/paymentInfoSlice'
import api from '../../../api'
import { Styles } from '../../../types'
import ResponsiveModal from '../../../rsmCoreComponents/components/ResponsiveModal'

interface ConfirmCancelAutopayDialogProps {
  open: boolean
  onClose: () => void
  handleCancelAutoPayResult: (success: boolean) => void
  clientId: string
}

const styles: Styles = {
  messageContainer: (theme) => ({
    [theme.breakpoints.down('tablet')]: {
      paddingBottom: '1.5rem',
    },
  }),
  title: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    marginBottom: '1rem',
    border: '0',
    textAlign: 'center',
    padding: '0.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '2rem',
      paddingTop: '1rem',
    },
  }),
  message: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    marginTop: '1rem',
    marginBottom: '1.5rem',
    border: '0',
    textAlign: 'center',
    [theme.breakpoints.up('tablet')]: {
      paddingLeft: '4.25rem',
      paddingRight: '4.25rem',
    },
  }),
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexFlow: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1.5rem',
    border: '0',
  },
}

const ConfirmCancelAutopayDialog = ({
  open,
  onClose,
  handleCancelAutoPayResult,
  clientId,
}: ConfirmCancelAutopayDialogProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const cemFeatures = useSelector(getCemFeatures)
  const unselectedStatus: SelectedAccountTypeState = 'Unselected'
  const defaultImplementAutoPayLimitType: ImplementAutoPayLimitType = 'No'
  const isAccountManager = useMemo(
    () =>
      isCemFeatureEnabled(
        CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const resetPaymentMethodState = () => {
    dispatch(setSelectedBankAccount(undefined))
    dispatch(setSelectedCreditCard(undefined))
    dispatch(setSelectedPayer(undefined))
    dispatch(setAccountSelectedType(unselectedStatus))
    dispatch(setNewPaymentMethod(false))
    dispatch(setDoNotSave(false))
    dispatch(setPayerPaymentAccountsModel([]))
    dispatch(setImplementAutoPayLimit(defaultImplementAutoPayLimitType))
  }

  const handleCancelAutopay = async () => {
    try {
      const clientIdInt = Number(clientId)
      const result = await api.finance.payment_CancelAutoPay(clientIdInt)

      if (result.data !== false) {
        resetPaymentMethodState()
        handleCancelAutoPayResult(true)
        onClose()
      } else {
        resetPaymentMethodState()
        handleCancelAutoPayResult(false)
        onClose()
      }
    } catch (error) {
      resetPaymentMethodState()
      handleCancelAutoPayResult(false)
      onClose()
    }
  }

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        overrideFullScreenDialog
        open={open}
        onClose={onClose}
        aria-label={t(
          'Invoicing.ConfirmCancelAutopayDialog.ConfirmAutopayCancellation',
        )}
        desktopMaxHeight="20.3125rem"
        desktopMaxWidth="44.375rem"
        removeHeaderSpacing
        data-testid="Lbl_Invoicing_ConfirmCancelAutopayDialog_Modal">
        <Box sx={styles.messageContainer}>
          <Box component="h1" sx={styles.title}>
            {t(
              'Invoicing.ConfirmCancelAutopayDialog.ConfirmAutopayCancellation',
            )}
          </Box>
          <Box sx={styles.message}>
            {isAccountManager
              ? t(
                  'Invoicing.ConfirmCancelAutopayDialog.MessageAccountManagement',
                )
              : t('Invoicing.ConfirmCancelAutopayDialog.MessageDashboard')}
          </Box>
          <Box sx={styles.buttonContainer}>
            <Button
              variant="outlined"
              data-testid="Btn_ConfirmCancelAutopayDialog_Close"
              onClick={onClose}>
              {t('Invoicing.ConfirmCancelAutopayDialog.Close')}
            </Button>
            <Button
              variant="contained"
              data-testid="Btn_ConfirmCancelAutopayDialog_Confirm"
              onClick={handleCancelAutopay}>
              {t('Invoicing.ConfirmCancelAutopayDialog.Confirm')}
            </Button>
          </Box>
        </Box>
      </ResponsiveModal>
    </Box>
  )
}

export default ConfirmCancelAutopayDialog
